/**
 * Helper central para resolução de chaves de contexto de densidade
 * 
 * Cada módulo/setor tem sua própria chave, garantindo que preferências
 * de densidade sejam independentes por módulo e por usuário.
 * 
 * Persistência: localStorage com chave `density-{userId}-{contextKey}`
 */

export type DensityContextKey = 
  | 'manager'
  | 'logistics'
  | 'arte'
  | 'comercial'
  | 'scheduling'
  | 'intelligence'
  | `operador-${string}`;

interface GetDensityContextKeyParams {
  pathname: string;
  userRole?: { setor?: string } | null;
}

/**
 * Resolve a chave de contexto de densidade baseado na rota e papel do usuário
 * 
 * @example
 * // No ManagerDashboard (rota /)
 * getDensityContextKey({ pathname: '/' }) // 'manager'
 * 
 * // No módulo Logística (rota /logistics)
 * getDensityContextKey({ pathname: '/logistics' }) // 'logistics'
 * 
 * // No OperatorDashboard para setor COMPRAS
 * getDensityContextKey({ pathname: '/operador', userRole: { setor: 'COMPRAS' } }) // 'operador-COMPRAS'
 */
export function getDensityContextKey({ pathname, userRole }: GetDensityContextKeyParams): DensityContextKey {
  // Normalizar pathname (remover trailing slash)
  const path = pathname.endsWith('/') && pathname !== '/' 
    ? pathname.slice(0, -1) 
    : pathname;

  // Rotas de logística
  if (path === '/logistics' || path.startsWith('/logistics/')) {
    return 'logistics';
  }

  // Rota comercial (vendedor)
  if (path === '/comercial' || path.startsWith('/comercial/')) {
    return 'comercial';
  }

  // Rota arte finalista
  if (path === '/arte' || path.startsWith('/arte/')) {
    return 'arte';
  }

  // Rota do operador - usa setor específico
  if (path === '/operador' || path.startsWith('/operador/')) {
    const setor = userRole?.setor || 'default';
    return `operador-${setor}`;
  }

  // Rotas de scheduling
  if (path === '/scheduling' || path.startsWith('/scheduling/')) {
    return 'scheduling';
  }

  // Rotas de intelligence
  if (path === '/intelligence' || path.startsWith('/intelligence/')) {
    return 'intelligence';
  }

  // Default: manager (dashboard principal)
  return 'manager';
}
